import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';
import { Brand, BrandCommonText, BrandCommonTextCategory } from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { groupBy } from '@go-shared/lib/functional';
import { capitalizeClean } from '@go-shared/lib/string';

// Custom titles for specific categories; any category not listed here falls back to an
// auto-generated title (see `categoryTitle`).
const CATEGORY_TITLES: Partial<Record<BrandCommonTextCategory, string>> = {
  groups_label: 'Group Labels',
};

function categoryTitle(category: string) {
  return CATEGORY_TITLES[category as BrandCommonTextCategory] ?? capitalizeClean(category, ['_']);
}

interface BrandCommonTextsFormDataBase {
  commonTexts: BrandCommonText[];
}

type BrandCommonTextsFormData = BrandCommonTextsFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandCommonTextsFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandCommonTextsFormData {
  return {
    commonTexts: brand?.commonTexts?.map(text => ({ ...text })) ?? [],
    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandCommonTextsForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandCommonTextsFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandCommonTextsFormDataBase>(
    key: T,
    val: BrandCommonTextsFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          commonTexts: formData.commonTexts,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const commonTextsByCategory = groupBy(formData.commonTexts, 'category');

  const formHtml = (
    <div>
      {Object.entries(commonTextsByCategory).map(([category, texts]) => (
        <div key={category} className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-navy">{categoryTitle(category)}</h3>
          <div className="grid grid-cols-3 gap-4">
            {texts.map(text => (
              <BubbleFormGroup key={text._id} label={text.label} className="col-span-3">
                <BubbleInput
                  type="text"
                  block
                  value={text.name}
                  onChange={value => {
                    const idx = formData.commonTexts.findIndex(s => s._id === text._id);
                    if (idx < 0) return;
                    const newElement = [...formData.commonTexts];
                    newElement[idx] = { ...newElement[idx], name: value.target.value };
                    changeForm('commonTexts', newElement);
                  }}
                />
              </BubbleFormGroup>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
